import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Socket, io } from 'socket.io-client';

import { APP_ENV } from '../configs';
import { getUniqueId } from '../utils/commonUtils';

interface SocketIoContextProps {
  sendMessage: (
    message: any,
    cb?: (...args: any) => void,
    timeout?: number
  ) => void;
  socketInstance: Socket | null;
  sessionId: string | null | undefined;
}

const defaultValue: SocketIoContextProps = {
  sendMessage: () => {},
  socketInstance: null,
  sessionId: null,
};
const uniqueId = getUniqueId();

const SocketIoContext = createContext<SocketIoContextProps>(defaultValue);

export const useSocketIoContext = () => useContext(SocketIoContext);

export const SocketIoProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const socketRef = useRef<Socket | null>(null);

  const [sessionId, setSessionId] = useState<string | null | undefined>(null);

  const sendMessage = useCallback(
    (message: string, cb?: (...args: any) => void, timeout: number = 0) => {
      if (socketRef.current !== null) {
        if (timeout)
          socketRef.current.timeout(timeout).emit('sot', message, cb);
        else socketRef.current.emit('sot', message, cb);
      }
    },
    []
  );

  useEffect(() => {
    // if (socketRef.current === null) {
    const socket = io(`${APP_ENV.WS_BASE_URL}`, {
      transports: ['websocket'],
      autoConnect: true,
      query: {
        client_id: uniqueId,
      },
    });

    //EVENTHANDLERS
    const handleConnect = () => {
      setSessionId(uniqueId);
      socket.emit('join', { client_id: uniqueId });
      socketRef.current = socket;
    };
    const handleDisconnect = () => {
      socketRef.current = null;
      setSessionId(null);
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socketRef.current = socket;
    return () => {
      socket.disconnect();
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socketRef.current = null;
      setSessionId(null);
    };
    // }
  }, []);

  // Memoize the context value to prevent unnecessary re-renders.
  const contextValue = useMemo(
    () => ({
      sessionId,
      sendMessage,
      socketInstance: socketRef.current,
    }),
    [sendMessage, sessionId]
  );

  return (
    <SocketIoContext.Provider value={contextValue}>
      {children}
    </SocketIoContext.Provider>
  );
};
